/**
 * Country name to ISO 3166-1 alpha-2 code mapping
 *
 * Maps full country names to their 2-letter ISO codes for flag display.
 * Covers common countries for SOKA users.
 *
 * Performance: Uses lazy initialization with Map for O(1) lookups.
 */

// Lazy-initialized Map for efficient lookups
let countryCodeMap: Map<string, string> | null = null;

const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  // Africa
  madagascar: 'mg',
  'south africa': 'za',
  nigeria: 'ng',
  kenya: 'ke',
  ghana: 'gh',
  ethiopia: 'et',
  egypt: 'eg',
  morocco: 'ma',
  algeria: 'dz',
  tunisia: 'tn',
  cameroon: 'cm',
  uganda: 'ug',
  tanzania: 'tz',
  senegal: 'sn',
  'burkina faso': 'bf',
  "côte d'ivoire": 'ci',
  'ivory coast': 'ci',
  rwanda: 'rw',
  zambia: 'zm',
  zimbabwe: 'zw',
  mozambique: 'mz',
  mali: 'ml',
  malawi: 'mw',
  niger: 'ne',
  chad: 'td',
  somalia: 'so',
  'congo (drc)': 'cd',
  'democratic republic of the congo': 'cd',
  'congo-kinshasa': 'cd',
  'congo (republic)': 'cg',
  'republic of the congo': 'cg',
  'congo-brazzaville': 'cg',

  // Europe
  france: 'fr',
  germany: 'de',
  'united kingdom': 'gb',
  uk: 'gb',
  britain: 'gb',
  'great britain': 'gb',
  spain: 'es',
  italy: 'it',
  portugal: 'pt',
  netherlands: 'nl',
  belgium: 'be',
  switzerland: 'ch',
  austria: 'at',
  poland: 'pl',
  ukraine: 'ua',
  romania: 'ro',
  greece: 'gr',
  sweden: 'se',
  norway: 'no',
  denmark: 'dk',
  finland: 'fi',
  ireland: 'ie',
  czechia: 'cz',
  'czech republic': 'cz',

  // North America
  'united states': 'us',
  usa: 'us',
  'united states of america': 'us',
  canada: 'ca',
  mexico: 'mx',

  // South America
  brazil: 'br',
  argentina: 'ar',
  colombia: 'co',
  chile: 'cl',
  peru: 'pe',
  venezuela: 've',

  // Asia
  china: 'cn',
  india: 'in',
  japan: 'jp',
  'south korea': 'kr',
  korea: 'kr',
  thailand: 'th',
  vietnam: 'vn',
  philippines: 'ph',
  indonesia: 'id',
  malaysia: 'my',
  singapore: 'sg',
  pakistan: 'pk',
  bangladesh: 'bd',
  'saudi arabia': 'sa',
  'united arab emirates': 'ae',
  uae: 'ae',
  israel: 'il',
  turkey: 'tr',

  // Oceania
  australia: 'au',
  'new zealand': 'nz'
};

/**
 * Initialize the country code Map (lazy initialization)
 * Only creates the Map on first use to avoid upfront cost
 */
function getCountryCodeMap(): Map<string, string> {
  countryCodeMap ??= new Map(Object.entries(COUNTRY_NAME_TO_CODE));
  return countryCodeMap;
}

/**
 * Convert country name to ISO 3166-1 alpha-2 code
 *
 * Uses lazy-initialized Map for O(1) lookups after first call.
 * Defensive: also handles if code is already provided (2-letter string).
 *
 * @param countryName - Full country name (e.g., "Madagascar", "United States")
 * @param fallback - Fallback code if country not found (default: "mg")
 * @returns 2-letter ISO country code (lowercase)
 *
 * @example
 * ```ts
 * getCountryCode("Madagascar") // "mg"
 * getCountryCode("United States") // "us"
 * getCountryCode("Unknown Country") // "mg" (fallback)
 * getCountryCode("mg") // "mg" (already a code)
 * ```
 */
export function getCountryCode(countryName: string | null | undefined, fallback = 'mg'): string {
  if (!countryName) {
    return fallback;
  }

  const normalized = countryName.toLowerCase().trim();

  // Defensive: If it's already a 2-letter code, return it
  // This handles edge cases where API might return codes instead of names
  if (normalized.length === 2 && /^[a-z]{2}$/.test(normalized)) {
    return normalized;
  }

  // Look up in Map (lazy-initialized on first call)
  return getCountryCodeMap().get(normalized) ?? fallback;
}
